import React, { useCallback, useEffect, useState } from 'react';
import { ArchiveBoxIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { runnerApi, errorText, type StuckItem, type StuckKind } from '../../../../api/migrationRunner';
import { Btn, Dialog, ErrorBox, Pill, Spinner, when, type Tone } from '../ui';
import { PairList, ResolveDialog, migName, plural, skippedText, today, type CleanupCtx, type ResolvePick } from './shared';

const LABEL: Record<StuckKind, string> = {
  running: 'Running', interrupted: 'Interrupted', partial: 'Partly applied', failed: 'Failed', locked: 'Locked',
  blocked: 'Blocked', skipped: 'Skipped', rolled_back: 'Rolled back', pending: 'Pending',
};
const TONE: Record<StuckKind, Tone> = {
  running: 'bad', interrupted: 'warn', partial: 'warn', failed: 'bad', locked: 'warn', blocked: 'warn', skipped: 'warn', rolled_back: 'mut', pending: 'mut',
};

function describe(i: StuckItem): string {
  switch (i.kind) {
    case 'running': return `Still marked running since ${when(i.lastRun)}. If nothing is running on ${i.companyName} any more, mark it stopped.`;
    case 'interrupted': return `Stopped part-way${i.failedIndex !== null ? ` at statement ${i.failedIndex + 1}` : ''}. ${i.reason || ''}`.trim();
    case 'partial': return `Some statements went in, then statement ${(i.failedIndex ?? 0) + 1} failed. ${i.reason || ''}`.trim();
    case 'failed': return i.errorKind === 'already_applied' ? `Failed because it is already there: ${i.reason || 'already exists'}. Usually safe to mark done.` : (i.reason || 'Failed with nothing applied.');
    case 'locked': return '"Another migration is already running" when it was tried. The lock is usually gone by now.';
    case 'blocked': return i.reason || "Blocked: some statements can't run twice (no IF NOT EXISTS).";
    case 'skipped': return i.reason || 'Skipped by a batch.';
    case 'rolled_back': return 'Rolled back, so it shows as pending again.';
    default: return 'Recorded as pending but never ran.';
  }
}

/** Clean up › Stuck (approved mockup screen 4): every state can be closed out. */
const StuckView: React.FC<{ ctx: CleanupCtx; refreshKey: number }> = ({ ctx, refreshKey }) => {
  const [items, setItems] = useState<StuckItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [resolving, setResolving] = useState<StuckItem | null>(null);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearNote, setClearNote] = useState('');
  const [clearing, setClearing] = useState(false);

  const load = useCallback(async () => {
    try {
      setItems(await runnerApi.stuck());
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Could not list stuck runs'));
    }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const run = async (i: StuckItem, name: string, fn: () => Promise<void>) => {
    setBusy(`${i.key}:${name}`);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const stop = (i: StuckItem) => run(i, 'stop', async () => {
    const r = await runnerApi.markStopped(i.runRowId!);
    setItems((list) => list?.filter((x) => x.key !== i.key) ?? null);
    ctx.notify('Marked interrupted. Resume is now on its run.', async () => {
      await runnerApi.undoMark(r.markId);
      ctx.notify('Undone.');
      ctx.changed();
    });
    ctx.changed();
  });

  const tryAgain = (i: StuckItem) => run(i, 'retry', async () => {
    if (i.runRowId && ['failed', 'partial', 'interrupted'].includes(i.kind)) {
      const b = await runnerApi.resume(i.runRowId);
      ctx.notify('Started again.');
      ctx.changed();
      ctx.nav.openBatch(b.id);
      return;
    }
    ctx.nav.openBatchDialog({ migrationIds: [i.scriptId], targets: [i.targetDb], onlyPairs: [`${i.scriptId}|${i.targetDb}`], title: `Try ${migName(i.folderPath)} on ${i.companyName} again` });
  });

  const archive = (i: StuckItem) => run(i, 'archive', async () => {
    const r = await runnerApi.archiveFolders([i.folder], 'Clean up: not needed anywhere');
    setItems((list) => list?.filter((x) => x.folder !== i.folder) ?? null);
    ctx.notify(`Folder archived (${r.archived} file${r.archived === 1 ? '' : 's'}). It leaves every list.`, async () => {
      await runnerApi.unarchivePaths(r.paths);
      ctx.notify('Undone.');
      ctx.changed();
    });
    ctx.changed();
  });

  const resolve = async (pick: ResolvePick, note: string) => {
    const i = resolving!;
    if (pick === 'later') {
      const r = await runnerApi.markStopped(i.runRowId!).catch((err) => { throw new Error(errorText(err)); });
      setResolving(null);
      setItems((list) => list?.filter((x) => x.key !== i.key) ?? null);
      ctx.notify('Marked stopped. It can be resumed or re-run.', async () => { await runnerApi.undoMark(r.markId); ctx.changed(); });
      ctx.changed();
      return;
    }
    const pair = i.runRowId ? { runRowId: i.runRowId } : { scriptId: i.scriptId, targetDb: i.targetDb };
    const r = await ctx.mark([pair], pick === 'done' ? 'applied_by_hand' : 'not_needed', note, () => 'Saved with your note.');
    if (!r.marked) throw new Error(r.skipped[0]?.reason === 'running' ? 'It is still running (fresh heartbeat). Wait for it, or Mark stopped once it stops.' : 'Nothing was marked.');
    setResolving(null);
    setItems((list) => list?.filter((x) => x.key !== i.key) ?? null);
  };

  const clearAll = async () => {
    setClearing(true);
    setError(null);
    try {
      const r = await runnerApi.clearStuck(clearNote.trim() || undefined);
      setClearOpen(false);
      const sr = r.skippedRunning;
      const kept = sr.lockHeld + sr.moving + sr.unknown;
      const markId = r.markId;
      ctx.notify(
        `Cleared ${plural(r.marked, 'stuck item')} as not needed.${kept ? ` Skipped ${kept} still running (${[sr.lockHeld && `${sr.lockHeld} lock held`, sr.moving && `${sr.moving} still moving`, sr.unknown && `${sr.unknown} couldn't check`].filter(Boolean).join(', ')}).` : ''}${skippedText({ ...r, inserted: 0, updated: 0 })}`,
        markId ? async () => { await runnerApi.undoMark(markId); ctx.notify('Undone.'); ctx.changed(); } : undefined,
      );
      ctx.changed();
    } catch (err) {
      setError(errorText(err, 'Could not clear'));
    } finally {
      setClearing(false);
    }
  };

  const w = !ctx.isOwner;
  const actions = (i: StuckItem) => {
    const b = (n: string) => busy === `${i.key}:${n}`;
    const resolveBtn = <Btn kind="ghost" small disabled={w || !ctx.ready} onClick={() => setResolving(i)}>Resolve…</Btn>;
    switch (i.kind) {
      case 'running':
        return <><Btn small disabled={w || !ctx.ready || b('stop') || !i.runRowId} onClick={() => stop(i)}>{b('stop') ? 'Checking…' : 'Mark stopped'}</Btn>{resolveBtn}</>;
      case 'rolled_back':
        return (
          <>
            <Btn kind="ghost" small disabled={w || !ctx.ready} onClick={() => setResolving(i)}>Not needed</Btn>
            <Btn kind="ghost" small disabled={w || b('archive')} onClick={() => archive(i)}><ArchiveBoxIcon className="h-4 w-4" /> Archive folder</Btn>
          </>
        );
      case 'blocked':
      case 'pending':
        return <><Btn kind="ghost" small onClick={() => ctx.nav.openPreview(i.scriptId)}>Preview</Btn>{resolveBtn}</>;
      default:
        return <><Btn small disabled={w || b('retry')} onClick={() => tryAgain(i)}>Try again</Btn>{resolveBtn}</>;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-bold text-slate-900">Stuck or can't finish</h2>
          <p className="text-[12.5px] text-slate-500">Every state can be closed out: running, interrupted, failed, skipped, locked, rolled back. Click a migration to open its run.</p>
        </div>
        {items && items.length > 0 && (
          <Btn kind="danger" small disabled={w || !ctx.ready} onClick={() => { setClearNote(''); setClearOpen(true); }}>Clear all stuck ({items.length})</Btn>
        )}
      </div>
      {error && <ErrorBox>{error}</ErrorBox>}
      {!items && !error && <div className="flex items-center gap-2 text-sm text-slate-500"><Spinner /> Loading…</div>}
      {items && !items.length && <div className="rounded-xl border border-slate-200 bg-white p-4 text-[13px] text-slate-500">Nothing stuck.</div>}
      {items && items.length > 0 && (
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <ul className="divide-y divide-slate-100">
            {items.map((i) => (
              <li key={i.key} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
                <div className="min-w-0 flex-1 text-[13px]">
                  <button
                    type="button"
                    className="font-mono text-[12px] font-bold text-slate-900 hover:text-blue-600 hover:underline disabled:no-underline"
                    disabled={!i.runRowId && !i.batchId}
                    onClick={() => (i.runRowId ? ctx.nav.openRun(i.runRowId) : i.batchId && ctx.nav.openBatch(i.batchId))}
                  >
                    {migName(i.folderPath)}
                  </button>{' '}
                  <span className="text-slate-500">on {i.companyName}</span> <Pill tone={TONE[i.kind]}>{LABEL[i.kind]}</Pill>
                  <div className="mt-0.5 text-[12.5px] text-slate-500">{describe(i)}</div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">{actions(i)}</div>
              </li>
            ))}
          </ul>
        </section>
      )}
      <div className="flex items-start gap-2 rounded-xl bg-amber-50 px-4 py-3 text-[13px] text-slate-700">
        <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <span><b>Runs left "running"</b> with no lock held and no progress for 30 minutes are marked <b>interrupted</b> on their own, so they show up here instead of hanging forever.</span>
      </div>

      <Dialog
        open={clearOpen}
        onClose={() => setClearOpen(false)}
        title={`Clear all ${items?.length ?? 0} stuck items`}
        footer={<><Btn kind="ghost" onClick={() => setClearOpen(false)}>Cancel</Btn><Btn kind="danger" onClick={clearAll} disabled={clearing}>{clearing ? 'Clearing…' : 'Clear all as not needed'}</Btn></>}
      >
        <p>Every item below is recorded as <b>not needed</b> (cleared, no longer needed) in one go. Runs still marked running are marked stopped first; any whose migration lock is really held, or that are still moving, are skipped and counted. Nothing runs on any database.</p>
        <PairList items={(items ?? []).map((i) => ({ key: i.key, folderPath: i.folderPath, companyName: `${i.companyName} (${LABEL[i.kind].toLowerCase()})` }))} />
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Note (optional)</span>
          <textarea value={clearNote} onChange={(e) => setClearNote(e.target.value)} rows={2} placeholder={`Cleared, no longer needed (Clear all stuck, ${today()}).`} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
        </label>
        <p className="text-xs text-slate-500">One mark: Undo puts every one back, for 30 days (History, Clean-up marks).</p>
      </Dialog>

      <ResolveDialog
        open={!!resolving}
        onClose={() => setResolving(null)}
        title={resolving ? `Resolve ${migName(resolving.folderPath)} on ${resolving.companyName}` : ''}
        state={resolving ? LABEL[resolving.kind] : ''}
        allowLater={resolving?.kind === 'running'}
        initial={resolving?.kind === 'rolled_back' || resolving?.kind === 'blocked' ? 'not_needed' : 'done'}
        onConfirm={resolve}
      />
    </div>
  );
};

export default StuckView;
